import { useMemo, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Pencil, Eye, Calendar, AlertTriangle, MoreHorizontal, Power, PowerOff, Send, Download, FileText, Loader2, Plus } from 'lucide-react'
import {
  fetchAdminLegalDocument,
  fetchAdminLegalDocuments,
  fetchDocumentTypes,
  toggleLegalDocumentStatus,
  updateLegalDocument,
  createLegalDocument,
  publishLegalDocument,
  type LegalDocument,
} from '../api'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { LegalDocumentEditor } from '../components/LegalDocumentEditor'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { Alert, AlertDescription } from '@/components/ui/alert'

import { PageHeader } from '@/components/app/PageHeader'
import { IconActionButton } from '@/components/ui/icon-action-button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { toast } from 'sonner'
import { exportDocumentPDF, exportDocumentDOCX } from '../lib/export'

interface LegalDocumentFormData {
  type: string
  business_id: string | null
  version: string
  content_fr: string
  content_en: string
  effective_at: string
}

const defaultFormData: LegalDocumentFormData = {
  type: 'platform_terms',
  business_id: null,
  version: '1.0',
  content_fr: '',
  content_en: '',
  effective_at: new Date().toISOString().split('T')[0],
}

export function AdminLegalDocumentsPage() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [previewFrContent, setPreviewFrContent] = useState('')
  const [previewEnContent, setPreviewEnContent] = useState('')
  const [previewDocType, setPreviewDocType] = useState('')
  const [activeTab, setActiveTab] = useState('fr')
  const [previewActiveTab, setPreviewActiveTab] = useState('fr')
  const [formData, setFormData] = useState<LegalDocumentFormData>(defaultFormData)
  const [publishDialogOpen, setPublishDialogOpen] = useState(false)
  const [publishingDoc, setPublishingDoc] = useState<{ id: string; type: string } | null>(null)
  const [exportingDocId, setExportingDocId] = useState<string | null>(null)
  const [exportingFormat, setExportingFormat] = useState<'pdf' | 'docx' | null>(null)

  const { data: documentsResponse, isLoading } = useQuery({
    queryKey: ['admin-legal-documents'],
    queryFn: () =>
      fetchAdminLegalDocuments({
        page: 1,
        per_page: 50,
      }),
  })

  const { data: documentTypes, isLoading: typesLoading } = useQuery({
    queryKey: ['document-types'],
    queryFn: fetchDocumentTypes,
  })

  const tableRows = useMemo(() => {
    if (!documentTypes?.data) return []
    return documentTypes.data.map((dt: { value: string; label: string }) => {
      const doc = documentsResponse?.data.find((d: LegalDocument) => d.type === dt.value)
      return { type: dt.value, label: dt.label, doc: doc || null }
    })
  }, [documentTypes, documentsResponse])

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<LegalDocumentFormData> }) =>
      updateLegalDocument(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-legal-documents'] })
      setDialogOpen(false)
      setFormData(defaultFormData)
      setEditingId(null)
    },
  })

  const createMutation = useMutation({
    mutationFn: createLegalDocument,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-legal-documents'] })
      queryClient.invalidateQueries({ queryKey: ['legal-documents'] })
      queryClient.invalidateQueries({ queryKey: ['public-legal-documents'] })
      setDialogOpen(false)
      setFormData(defaultFormData)
      setEditingId(null)
      toast.success(t('admin.legalDocuments.created', 'Document created successfully'))
    },
  })

  const toggleStatusMutation = useMutation({
    mutationFn: toggleLegalDocumentStatus,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-legal-documents'] })
      queryClient.invalidateQueries({ queryKey: ['legal-documents'] })
      queryClient.invalidateQueries({ queryKey: ['public-legal-documents'] })
    },
  })

  const publishMutation = useMutation({
    mutationFn: publishLegalDocument,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-legal-documents'] })
      queryClient.invalidateQueries({ queryKey: ['legal-documents'] })
      queryClient.invalidateQueries({ queryKey: ['public-legal-documents'] })
      setPublishDialogOpen(false)
      setPublishingDoc(null)
    },
  })

  const handleSubmit = () => {
    if (editingId) {
      updateMutation.mutate({ id: editingId, data: formData })
    } else {
      createMutation.mutate(formData)
    }
  }

  const handleCreate = (type: string) => {
    setEditingId(null)
    setFormData({
      ...defaultFormData,
      type,
      content_fr: '',
      content_en: '',
    })
    setActiveTab('fr')
    setDialogOpen(true)
  }

  const handleEdit = async (doc: {
    id: string
    type: string
    version: string
    effective_at: string
    has_fr: boolean
    has_en: boolean
    fr_id?: string
    en_id?: string
  }) => {
    setEditingId(doc.id)
    
    // Fetch existing content for both languages
    let contentFr = ''
    let contentEn = ''
    
    if (doc.fr_id) {
      try {
        const frDoc = await fetchAdminLegalDocument(doc.fr_id)
        contentFr = frDoc.data.edit_content || frDoc.data.content || ''
      } catch {
        contentFr = ''
      }
    }

    if (doc.en_id) {
      try {
        const enDoc = await fetchAdminLegalDocument(doc.en_id)
        contentEn = enDoc.data.edit_content || enDoc.data.content || ''
      } catch {
        contentEn = ''
      }
    }
    
    setFormData({
      type: doc.type,
      business_id: null,
      version: doc.version,
      content_fr: contentFr,
      content_en: contentEn,
      effective_at: doc.effective_at.split('T')[0],
    })
    setActiveTab('fr')
    setDialogOpen(true)
  }

  const handleToggleStatus = (id: string) => {
    toggleStatusMutation.mutate(id)
  }

  const handleExport = async (
    doc: {
      id: string
      type: string
      has_fr: boolean
      has_en: boolean
      fr_id?: string
      en_id?: string
    },
    format: 'pdf' | 'docx',
    locale: 'fr' | 'en'
  ) => {
    const docId = locale === 'fr' ? doc.fr_id : doc.en_id
    if (!docId) {
      toast.error(t('admin.legalDocuments.exportNoContent', 'No content available'))
      return
    }
    setExportingDocId(doc.id)
    setExportingFormat(format)
    try {
      const res = await fetchAdminLegalDocument(docId)
      const html = res.data.draft_content || res.data.content || ''
      if (!html || html === '<p></p>') {
        toast.error(t('admin.legalDocuments.exportEmpty', 'Document is empty'))
        return
      }
      const typeLabel = getTypeLabel(doc.type)
      const filename = `${typeLabel}-${locale}`.replace(/\s+/g, '-').toLowerCase()
      if (format === 'pdf') {
        await exportDocumentPDF(html, filename)
        toast.success(t('admin.legalDocuments.editor.exportSuccess', 'Exported successfully'), {
          description: t('admin.legalDocuments.editor.exportPDFSuccess', 'PDF downloaded.'),
        })
      } else {
        await exportDocumentDOCX(html, filename)
        toast.success(t('admin.legalDocuments.editor.exportSuccess', 'Exported successfully'), {
          description: t('admin.legalDocuments.editor.exportDOCXSuccess', 'DOCX downloaded.'),
        })
      }
    } catch {
      toast.error(t('admin.legalDocuments.editor.exportError', 'Export failed'), {
        description: t('admin.legalDocuments.exportFetchError', 'Could not fetch document content. Try again.'),
      })
    } finally {
      setExportingDocId(null)
      setExportingFormat(null)
    }
  }

  const handlePreview = async (doc: {
    id: string
    type: string
    has_fr: boolean
    has_en: boolean
    fr_id?: string
    en_id?: string
  }) => {
    setPreviewDocType(doc.type)
    setPreviewActiveTab('fr')
    
    // Fetch both language versions (show draft if available)
    if (doc.fr_id) {
      try {
        const frDoc = await fetchAdminLegalDocument(doc.fr_id)
        setPreviewFrContent(frDoc.data.draft_content || frDoc.data.content || `<p>${t('admin.legalDocuments.contentNotAvailable', 'Content not available')}</p>`)
      } catch {
        setPreviewFrContent(`<p>${t('admin.legalDocuments.contentNotAvailable', 'Content not available')}</p>`)
      }
    } else {
      setPreviewFrContent(`<p>${t('admin.legalDocuments.contentNotAvailable', 'Content not available')}</p>`)
    }

    if (doc.en_id) {
      try {
        const enDoc = await fetchAdminLegalDocument(doc.en_id)
        setPreviewEnContent(enDoc.data.draft_content || enDoc.data.content || `<p>${t('admin.legalDocuments.contentNotAvailable', 'Content not available')}</p>`)
      } catch {
        setPreviewEnContent(`<p>${t('admin.legalDocuments.contentNotAvailable', 'Content not available')}</p>`)
      }
    } else {
      setPreviewEnContent(`<p>${t('admin.legalDocuments.contentNotAvailable', 'Content not available')}</p>`)
    }
    
    setPreviewOpen(true)
  }

  const getTypeLabel = (type: string) => {
    return t(`admin.legalDocuments.types.${type}` as const, type)
  }

  const getTypeBadgeClass = (type: string): string => {
    if (type.startsWith('platform_')) {
      return 'bg-blue-500/15 text-blue-700 dark:text-blue-300 border-transparent'
    }
    if (type.startsWith('agent_')) {
      return 'bg-purple-500/15 text-purple-700 dark:text-purple-300 border-transparent'
    }
    return 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-transparent'
  }

  const getLanguageBadgeClass = (hasLanguage: boolean): string => {
    if (hasLanguage) {
      return 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-transparent'
    }
    return 'bg-muted text-muted-foreground border-transparent'
  }

  const getStatusBadge = (doc: {
    is_active: boolean
    has_draft?: boolean
  }): { label: string; className: string } => {
    if (!doc.is_active) {
      return {
        label: t('admin.legalDocuments.status.inactive', 'Inactive'),
        className: 'bg-gray-500/15 text-gray-700 dark:text-gray-300 border-transparent',
      }
    }
    if (doc.has_draft) {
      return {
        label: t('admin.legalDocuments.status.hasDraft', 'Has Draft'),
        className: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-transparent',
      }
    }
    return {
      label: t('admin.legalDocuments.status.live', 'Live'),
      className: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-transparent',
    }
  }

  const injectLinkTargets = useCallback((html: string): string => {
    return html.replace(/<a\b([^>]*)>/gi, (match, attrs: string) => {
      if (/\btarget\s*=/i.test(attrs)) return match
      const separator = attrs.trim().length > 0 && !attrs.endsWith(' ') ? ' ' : ''
      return `<a${attrs}${separator}target="_blank" rel="noopener noreferrer">`
    })
  }, [])



  return (
    <div className="space-y-6">
      <PageHeader
        title={t('admin.legalDocuments.title', 'Legal Documents')}
      />

      <article className="rounded-lg bg-card p-3 sm:p-4">
        <div className="overflow-hidden rounded-lg border border-border">
          <div className="overflow-x-auto">
            {isLoading || typesLoading ? (
              <div className="p-4 space-y-2">
                {Array.from({ length: documentTypes?.data?.length || 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[12rem]">
                      {t('admin.legalDocuments.table.type', 'Type')}
                    </TableHead>
                    <TableHead className="hidden sm:table-cell w-[10rem]">
                      {t('admin.legalDocuments.table.languages', 'Languages')}
                    </TableHead>
                    <TableHead className="w-10 pe-2 text-end">
                      <span className="sr-only">{t('common.actions', 'Actions')}</span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tableRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center text-muted-foreground py-8">
                        {t('admin.legalDocuments.noDocuments', 'No legal documents found.')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    tableRows.map((row) => {
                      const doc = row.doc
                      if (!doc) {
                        return (
                          <TableRow key={row.type}>
                            <TableCell>
                              <div className="flex flex-col gap-1.5">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <Badge
                                    variant="outline"
                                    className={`w-fit capitalize ${getTypeBadgeClass(row.type)}`}
                                  >
                                    {getTypeLabel(row.type)}
                                  </Badge>
                                  <Badge variant="outline" className="text-[10px] bg-gray-500/15 text-gray-700 dark:text-gray-300 border-transparent">
                                    {t('admin.legalDocuments.status.notCreated', 'Not created')}
                                  </Badge>
                                </div>
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                  <span>—</span>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="hidden sm:table-cell">
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className="text-xs bg-muted text-muted-foreground border-transparent">
                                  FR
                                </Badge>
                                <Badge variant="outline" className="text-xs bg-muted text-muted-foreground border-transparent">
                                  EN
                                </Badge>
                              </div>
                            </TableCell>
                            <TableCell className="pe-2 text-end">
                              <Button size="sm" variant="outline" onClick={() => handleCreate(row.type)}>
                                <Plus className="mr-1.5 size-3.5" />
                                {t('admin.legalDocuments.actions.create', 'Create')}
                              </Button>
                            </TableCell>
                          </TableRow>
                        )
                      }
                      return (
                        <TableRow key={doc.id}>
                          <TableCell>
                            <div className="flex flex-col gap-1.5">
                              <div className="flex items-center gap-2 flex-wrap">
                                <Badge
                                  variant="outline"
                                  className={`w-fit capitalize ${getTypeBadgeClass(doc.type)}`}
                                >
                                  {getTypeLabel(doc.type)}
                                </Badge>
                                {(() => {
                                  const status = getStatusBadge(doc)
                                  return (
                                    <Badge variant="outline" className={`text-[10px] ${status.className}`}>
                                      {status.label}
                                    </Badge>
                                  )
                                })()}
                              </div>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <Calendar className="h-3 w-3" />
                                <span>
                                  v{doc.version} —{' '}
                                  {new Date(doc.effective_at).toLocaleDateString()}
                                </span>
                              </div>
                            </div>
                          </TableCell>

                          <TableCell className="hidden sm:table-cell">
                            <div className="flex items-center gap-2">
                              <Badge
                                variant="outline"
                                className={`text-xs ${getLanguageBadgeClass(doc.has_fr)}`}
                              >
                                {t('admin.legalDocuments.badge.fr', 'FR')}
                              </Badge>
                              <Badge
                                variant="outline"
                                className={`text-xs ${getLanguageBadgeClass(doc.has_en)}`}
                              >
                                {t('admin.legalDocuments.badge.en', 'EN')}
                              </Badge>
                            </div>
                          </TableCell>

                          <TableCell className="pe-2 text-end">
                            <div className="hidden items-center justify-end gap-2 md:flex">
                              <div className="flex items-center gap-2 mr-2">
                                {doc.type.startsWith('platform_') ? (
                                  <span className="text-xs text-muted-foreground">
                                    {t('admin.legalDocuments.alwaysActive', 'Always active')}
                                  </span>
                                ) : (
                                  <>
                                    <Switch
                                      checked={doc.is_active}
                                      onCheckedChange={() => handleToggleStatus(doc.id)}
                                      disabled={toggleStatusMutation.isPending}
                                      aria-label={
                                        doc.is_active
                                          ? t('admin.legalDocuments.actions.disable', 'Disable document')
                                          : t('admin.legalDocuments.actions.enable', 'Enable document')
                                      }
                                    />
                                    <span className="text-xs text-muted-foreground">
                                      {doc.is_active
                                        ? t('common.active', 'Active')
                                        : t('common.inactive', 'Inactive')}
                                    </span>
                                  </>
                                )}
                              </div>
                              <IconActionButton
                                icon={<Eye className="size-4" />}
                                tooltip={t('admin.legalDocuments.actions.preview', 'Preview document')}
                                onClick={() => handlePreview(doc)}
                              />
                              <IconActionButton
                                icon={<Pencil className="size-4" />}
                                tooltip={t('admin.legalDocuments.actions.edit', 'Edit document')}
                                tone="primary"
                                onClick={() => handleEdit(doc)}
                              />
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon-sm"
                                    className="text-muted-foreground"
                                    disabled={exportingDocId === doc.id}
                                    aria-label={t('admin.legalDocuments.actions.export', 'Export document')}
                                  >
                                    {exportingDocId === doc.id ? (
                                      <Loader2 className="size-4 animate-spin" />
                                    ) : (
                                      <Download className="size-4" />
                                    )}
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="min-w-[10rem]">
                                  {doc.has_fr && (
                                    <>
                                      <DropdownMenuItem
                                        disabled={exportingDocId === doc.id && exportingFormat === 'pdf'}
                                        onClick={() => handleExport(doc, 'pdf', 'fr')}
                                      >
                                        <FileText className="mr-2 size-4 text-primary" />
                                        <span>PDF (FR)</span>
                                      </DropdownMenuItem>
                                      <DropdownMenuItem
                                        disabled={exportingDocId === doc.id && exportingFormat === 'docx'}
                                        onClick={() => handleExport(doc, 'docx', 'fr')}
                                      >
                                        <FileText className="mr-2 size-4 text-primary" />
                                        <span>DOCX (FR)</span>
                                      </DropdownMenuItem>
                                    </>
                                  )}
                                  {doc.has_fr && doc.has_en && <DropdownMenuSeparator />}
                                  {doc.has_en && (
                                    <>
                                      <DropdownMenuItem
                                        disabled={exportingDocId === doc.id && exportingFormat === 'pdf'}
                                        onClick={() => handleExport(doc, 'pdf', 'en')}
                                      >
                                        <FileText className="mr-2 size-4 text-primary" />
                                        <span>PDF (EN)</span>
                                      </DropdownMenuItem>
                                      <DropdownMenuItem
                                        disabled={exportingDocId === doc.id && exportingFormat === 'docx'}
                                        onClick={() => handleExport(doc, 'docx', 'en')}
                                      >
                                        <FileText className="mr-2 size-4 text-primary" />
                                        <span>DOCX (EN)</span>
                                      </DropdownMenuItem>
                                    </>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                              {doc.has_draft && doc.is_active && (
                                <IconActionButton
                                  icon={<Send className="size-4" />}
                                  tooltip={t('admin.legalDocuments.actions.publish', 'Publish & Notify')}
                                  tone="success"
                                  onClick={() => {
                                    setPublishingDoc({ id: doc.id, type: doc.type })
                                    setPublishDialogOpen(true)
                                  }}
                                />
                              )}
                            </div>

                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon-sm"
                                  className="text-muted-foreground md:hidden"
                                  aria-label={t('common.actions')}
                                >
                                  <MoreHorizontal className="size-4" aria-hidden />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="min-w-[10rem]">
                                {!doc.type.startsWith('platform_') && (
                                  <>
                                    <DropdownMenuItem onClick={() => handleToggleStatus(doc.id)}>
                                      {doc.is_active ? (
                                        <>
                                          <PowerOff className="size-4 text-amber-500" />
                                          <span>{t('admin.legalDocuments.actions.disable')}</span>
                                        </>
                                      ) : (
                                        <>
                                          <Power className="size-4 text-emerald-500" />
                                          <span>{t('admin.legalDocuments.actions.enable')}</span>
                                        </>
                                      )}
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                  </>
                                )}
                                <DropdownMenuItem onClick={() => handlePreview(doc)}>
                                  <Eye className="size-4 text-primary" />
                                  <span>{t('admin.legalDocuments.actions.preview')}</span>
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleEdit(doc)}>
                                  <Pencil className="size-4 text-primary" />
                                  <span>{t('admin.legalDocuments.actions.edit')}</span>
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem disabled={exportingDocId === doc.id} onClick={() => handleExport(doc, 'pdf', 'fr')}>
                                  <FileText className="mr-2 size-4 text-primary" />
                                  <span>PDF (FR)</span>
                                </DropdownMenuItem>
                                <DropdownMenuItem disabled={exportingDocId === doc.id} onClick={() => handleExport(doc, 'docx', 'fr')}>
                                  <FileText className="mr-2 size-4 text-primary" />
                                  <span>DOCX (FR)</span>
                                </DropdownMenuItem>
                                <DropdownMenuItem disabled={exportingDocId === doc.id} onClick={() => handleExport(doc, 'pdf', 'en')}>
                                  <FileText className="mr-2 size-4 text-primary" />
                                  <span>PDF (EN)</span>
                                </DropdownMenuItem>
                                <DropdownMenuItem disabled={exportingDocId === doc.id} onClick={() => handleExport(doc, 'docx', 'en')}>
                                  <FileText className="mr-2 size-4 text-primary" />
                                  <span>DOCX (EN)</span>
                                </DropdownMenuItem>
                                {doc.has_draft && doc.is_active && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      onClick={() => {
                                        setPublishingDoc({ id: doc.id, type: doc.type })
                                        setPublishDialogOpen(true)
                                      }}
                                    >
                                      <Send className="size-4 text-emerald-500" />
                                      <span>{t('admin.legalDocuments.actions.publish', 'Publish & Notify')}</span>
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      )
                    })
                  )}
                </TableBody>
              </Table>
            )}
          </div>

          {/* All document types fit on one screen — pagination not needed */}
        </div>
      </article>

      {/* Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="w-[95vw] md:w-[85vw] lg:w-[60vw] lg:max-w-[1200px] max-h-[90vh] flex flex-col p-0 gap-0 overflow-hidden">
          <DialogHeader className="px-6 pt-6 pb-2 shrink-0">
            <DialogTitle>
              {editingId
                ? t('admin.legalDocuments.editDocument', 'Edit Document')
                : t('admin.legalDocuments.createDocument', 'Create Document')
              }
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 min-h-0 overflow-y-auto px-6 py-2 space-y-4">
            <Alert className="bg-amber-500/10 border-amber-500/20 text-amber-800 dark:text-amber-300 shrink-0">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertDescription>
                {t('admin.legalDocuments.dialog.warning', 'Both language versions are required and should be kept consistent. Users will see content based on their selected language.')}
              </AlertDescription>
            </Alert>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('admin.legalDocuments.version', 'Version')}</Label>
                <Input
                  value={formData.version}
                  onChange={(e) => setFormData((prev) => ({ ...prev, version: e.target.value }))}
                  placeholder="1.0"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('admin.legalDocuments.effectiveDate', 'Effective Date')}</Label>
                <Input
                  type="date"
                  value={formData.effective_at}
                  onChange={(e) => setFormData((prev) => ({ ...prev, effective_at: e.target.value }))}
                />
              </div>
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="fr">
                  {t('admin.legalDocuments.dialog.frTab', 'Français')}
                </TabsTrigger>
                <TabsTrigger value="en">
                  {t('admin.legalDocuments.dialog.enTab', 'English')}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="fr" className="space-y-2 mt-2">
                <Label>
                  {t('admin.legalDocuments.dialog.contentFr', 'Content (French)')}
                </Label>
                <LegalDocumentEditor
                  value={formData.content_fr}
                  onChange={(value) => setFormData((prev) => ({ ...prev, content_fr: value }))}
                  placeholder={t('admin.legalDocuments.dialog.placeholderFr', '<h2>Titre de la section</h2>\n<p>Votre contenu ici...</p>')}
                />
              </TabsContent>

              <TabsContent value="en" className="space-y-2 mt-2">
                <Label>
                  {t('admin.legalDocuments.dialog.contentEn', 'Content (English)')}
                </Label>
                <LegalDocumentEditor
                  value={formData.content_en}
                  onChange={(value) => setFormData((prev) => ({ ...prev, content_en: value }))}
                  placeholder={t('admin.legalDocuments.dialog.placeholderEn', '<h2>Section Title</h2>\n<p>Your content here...</p>')}
                />
              </TabsContent>
            </Tabs>
          </div>

          <DialogFooter className="px-6 py-4 border-t shrink-0">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={
                !formData.version ||
                !formData.content_fr ||
                !formData.content_en ||
                !formData.effective_at ||
                updateMutation.isPending ||
                createMutation.isPending
              }
            >
              {updateMutation.isPending || createMutation.isPending
                ? t('common.saving', 'Saving...')
                : editingId
                  ? t('admin.legalDocuments.saveDraft', 'Save Draft')
                  : t('admin.legalDocuments.createDocument', 'Create Document')
              }
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Publish Confirmation Dialog */}
      <Dialog open={publishDialogOpen} onOpenChange={setPublishDialogOpen}>
        <DialogContent className="w-[95vw] sm:w-[32rem]">
          <DialogHeader>
            <DialogTitle>
              {t('admin.legalDocuments.publishConfirmTitle', 'Publish New Version?')}
            </DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <p className="text-sm text-muted-foreground">
              {t('admin.legalDocuments.publishConfirmDescription', 'This will push your draft live and require all affected users to review and accept the updated terms.')}
            </p>
          </div>
          <DialogFooter className="flex-row gap-2 sm:justify-end">
            <Button variant="outline" onClick={() => setPublishDialogOpen(false)}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              onClick={() => {
                if (publishingDoc) {
                  publishMutation.mutate(publishingDoc.id)
                }
              }}
              disabled={publishMutation.isPending}
            >
              {publishMutation.isPending
                ? t('common.processing', 'Processing...')
                : t('admin.legalDocuments.publishAndNotify', 'Publish & Notify')
              }
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="w-[95vw] md:w-[85vw] lg:w-[60vw] lg:max-w-[1200px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {t('admin.legalDocuments.preview', 'Preview')} — {getTypeLabel(previewDocType)}
            </DialogTitle>
          </DialogHeader>
          <Tabs value={previewActiveTab} onValueChange={setPreviewActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="fr">
                {t('admin.legalDocuments.dialog.frTab', 'Français')}
              </TabsTrigger>
              <TabsTrigger value="en">
                {t('admin.legalDocuments.dialog.enTab', 'English')}
              </TabsTrigger>
            </TabsList>
            <TabsContent value="fr">
              <div
                className="prose prose-sm max-w-none dark:prose-invert py-4
                  prose-headings:mt-8 prose-headings:mb-3
                  prose-h2:text-xl prose-h2:font-semibold
                  prose-p:my-0 prose-p:leading-7
                  prose-ul:my-0 prose-ul:space-y-0
                  prose-ol:my-0 prose-ol:space-y-0
                  prose-li:my-0"
                dangerouslySetInnerHTML={{ __html: injectLinkTargets(previewFrContent) }}
              />
            </TabsContent>
            <TabsContent value="en">
              <div
                className="prose prose-sm max-w-none dark:prose-invert py-4
                  prose-headings:mt-8 prose-headings:mb-3
                  prose-h2:text-xl prose-h2:font-semibold
                  prose-p:my-0 prose-p:leading-7
                  prose-ul:my-0 prose-ul:space-y-0
                  prose-ol:my-0 prose-ol:space-y-0
                  prose-li:my-0"
                dangerouslySetInnerHTML={{ __html: injectLinkTargets(previewEnContent) }}
              />
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>


    </div>
  )
}